import { Link } from "react-router-dom";
import SiteNavbar from "@/components/feature/SiteNavbar";
import SiteFooter from "@/components/feature/SiteFooter";
import WorkCard from "@/components/feature/WorkCard";
import HeroCarousel from "./components/HeroCarousel";
import { categories } from "@/mocks/categories";
import { works } from "@/mocks/works";
import { inspirations } from "@/mocks/inspirations";
import { useSeo } from "@/hooks/useSeo";
import type { Work } from "@/types";

export default function Home() {
  useSeo({
    title: "女性ins大片-时尚女性摄影作品展示与灵感分享",
    description:
      "女性ins大片网站专注于展示最新女性INS风格大片，涵盖时尚、艺术摄影等视觉内容，帮助用户获取拍摄灵感和欣赏高品质女性摄影作品。",
    keywords: "女性ins大片,INS风格摄影,时尚女性大片,女性摄影作品,时尚大片分享,摄影灵感,女性视觉艺术",
    path: "/",
  });

  const featured = (works as Work[]).filter((w) => w.featured).slice(0, 4);
  const latest = [...(works as Work[])]
    .sort((a, b) => (a.date < b.date ? 1 : -1))
    .slice(0, 12);

  return (
    <div className="min-h-screen bg-background-50">
      <SiteNavbar overlay />
      <main>
        <HeroCarousel items={featured} />

        {/* Category entries */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 md:mb-10">
              <div>
                <span className="font-label text-xs tracking-[0.25em] uppercase text-primary-500">
                  Categories
                </span>
                <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mt-2">
                  找到你喜欢的风格
                </h2>
              </div>
              <p className="text-sm text-foreground-600 max-w-md">
                从时尚到艺术，从生活到创意，四大分类帮你快速定位心仪的女性摄影作品。
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {categories.map((cat, i) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.id}`}
                  className="group relative rounded-lg overflow-hidden bg-background-50 cursor-pointer animate-fade-up"
                  style={{ animationDelay: `${i * 60}ms` }}
                >
                  <div className="relative w-full aspect-[4/5]">
                    <img
                      src={cat.cover}
                      alt={`${cat.name} - 女性ins大片分类`}
                      title={`${cat.name} - 时尚女性摄影分类`}
                      className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/20 to-transparent" />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <span className="w-10 h-10 rounded-full bg-primary-500/90 flex items-center justify-center mb-3">
                      <i className={`${cat.icon} text-background-50 text-lg`}></i>
                    </span>
                    <h3 className="font-heading text-lg font-semibold text-background-50">
                      {cat.name}
                    </h3>
                    <p className="text-xs text-background-100/80 mt-1 line-clamp-2">
                      {cat.description}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Latest works */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 md:mb-10">
              <div>
                <span className="font-label text-xs tracking-[0.25em] uppercase text-primary-500">
                  Latest
                </span>
                <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mt-2">
                  最新作品
                </h2>
              </div>
              <Link
                to="/category/fashion"
                className="text-sm font-medium text-primary-600 hover:text-primary-700 flex items-center gap-1 cursor-pointer whitespace-nowrap"
              >
                浏览全部作品
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            {latest.length === 0 ? (
              <p className="text-center text-foreground-500 py-16">暂无最新作品，敬请期待</p>
            ) : (
              <div
                data-product-shop
                className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6"
              >
                {latest.map((work, i) => (
                  <WorkCard key={work.id} work={work} index={i} />
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Inspiration preview */}
        <section className="py-14 md:py-20 bg-secondary-50">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 md:mb-10">
              <div>
                <span className="font-label text-xs tracking-[0.25em] uppercase text-primary-500">
                  Inspiration
                </span>
                <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mt-2">
                  推荐灵感专题
                </h2>
              </div>
              <Link
                to="/inspiration"
                className="text-sm font-medium text-primary-600 hover:text-primary-700 flex items-center gap-1 cursor-pointer whitespace-nowrap"
              >
                查看全部专题
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
              {inspirations.slice(0, 3).map((ins, i) => (
                <Link
                  key={ins.id}
                  to={`/inspiration?topic=${ins.id}`}
                  className="group rounded-lg overflow-hidden bg-background-50 cursor-pointer animate-fade-up"
                  style={{ animationDelay: `${i * 60}ms` }}
                >
                  <div className="relative w-full aspect-[3/2] overflow-hidden">
                    <img
                      src={ins.cover}
                      alt={`${ins.title} - 摄影灵感专题`}
                      title={`${ins.title} - 女性摄影灵感专题`}
                      className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                    />
                    <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-accent-500 text-foreground-950 text-[11px] font-medium">
                      灵感专题
                    </span>
                  </div>
                  <div className="p-5">
                    <h3 className="font-heading text-lg font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors">
                      {ins.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-1 mb-2">{ins.subtitle}</p>
                    <p className="text-sm text-foreground-600 line-clamp-2">{ins.intro}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Brand intro */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center">
              <div className="relative w-full aspect-[4/3] rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=warm%20toned%20photography%20studio%20scene%20with%20soft%20natural%20light%2C%20camera%20and%20prints%20on%20a%20cream%20table%2C%20artistic%20minimal%20aesthetic%2C%20high%20detail&width=1200&height=900&seq=home-brand-01&orientation=landscape"
                  alt="女性ins大片 - 关于我们的摄影工作场景"
                  title="女性ins大片 - 摄影灵感工作室"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div>
                <span className="font-label text-xs tracking-[0.25em] uppercase text-primary-500">
                  About Us
                </span>
                <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mt-2 mb-5">
                  用影像，记录女性的每一种可能
                </h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  「女性ins大片」是一个专注女性视觉艺术的摄影作品展示平台。我们汇聚时尚大片、艺术摄影、生活写真与创意特辑四大方向，为摄影爱好者、时尚女性与视觉创作者提供一个发现灵感、交流美感的窗口。
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-8">
                  我们相信，每一张照片都是一次自我表达。无论是霓虹下的率性，还是暖光里的温柔，女性的美从不只有一种答案。
                </p>
                <div className="grid grid-cols-3 gap-4 mb-8">
                  {[
                    { num: "1800+", label: "精选作品" },
                    { num: "120+", label: "合作摄影师" },
                    { num: "36", label: "灵感专题" },
                  ].map((stat) => (
                    <div key={stat.label}>
                      <p className="font-heading text-2xl md:text-3xl font-bold text-primary-500">
                        {stat.num}
                      </p>
                      <p className="text-xs text-foreground-500 mt-1">{stat.label}</p>
                    </div>
                  ))}
                </div>
                <Link
                  to="/about"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  了解更多
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}